import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { IoShieldCheckmark, IoClose, IoSaveOutline } from 'react-icons/io5';
import { fetchRoles, getUserRoles, assignRoleToUser, revokeRoleFromUser } from '../../services/api';

export const UserRolesModal = ({ isOpen, onClose, user, userIdToken }) => {
    const [allRoles, setAllRoles] = useState([]);
    const [userAssignedRoles, setUserAssignedRoles] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    // Checkbox toggles tracking for saving delta. 
    // True means selected (to be assigned or already assigned), False means not selected
    const [selectedRoleIds, setSelectedRoleIds] = useState(new Set());

    useEffect(() => {
        if (isOpen && user) {
            loadInitialData();
        }
    }, [isOpen, user]);

    const loadInitialData = async () => {
        setIsLoading(true);
        try {
            const userId = user.id || user._id || user.user_id;
            const [rolesRes, userRolesRes] = await Promise.all([
                fetchRoles(userIdToken),
                getUserRoles(userIdToken, userId)
            ]);

            setAllRoles(rolesRes.data || []);
            const userRoles = userRolesRes.data || [];
            setUserAssignedRoles(userRoles);

            // Populate the selected set with the IDs of currently assigned roles
            const initialSet = new Set(userRoles.map(ur => ur.role_id || (ur.role && (ur.role.id || ur.role._id))));
            setSelectedRoleIds(initialSet);
        } catch (error) {
            toast.error(`Failed to load roles: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen || !user) return null;

    const handleToggleRole = (roleId) => {
        const newSet = new Set(selectedRoleIds);
        if (newSet.has(roleId)) {
            newSet.delete(roleId);
        } else {
            newSet.add(roleId);
        }
        setSelectedRoleIds(newSet);
    };

    const handleSave = async () => {
        setIsSaving(true);
        const originalAssignedIds = new Set(userAssignedRoles.map(ur => ur.role_id || (ur.role && (ur.role.id || ur.role._id))));
        const userId = user.id || user._id || user.user_id;

        // Find which roles to add and which to remove
        const rolesToAdd = [...selectedRoleIds].filter(id => !originalAssignedIds.has(id));
        const rolesToRemove = [...originalAssignedIds].filter(id => !selectedRoleIds.has(id));

        try {
            // Execute all removals and additions in parallel mapping to promises
            const removePromises = rolesToRemove.map(roleId => revokeRoleFromUser(userIdToken, userId, roleId));
            const addPromises = rolesToAdd.map(roleId => assignRoleToUser(userIdToken, userId, roleId));

            await Promise.all([...removePromises, ...addPromises]);
            toast.success("User roles updated successfully");
            onClose();
        } catch (error) {
            toast.error(`Error updating roles: ${error.message}`);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm animate-fade-in p-4">
            <div className="bg-1 rounded-xl shadow-2xl w-full max-w-lg border border-line animate-fade-in-up flex flex-col max-h-[90vh]">

                {/* Header */}
                <div className="flex items-center justify-between p-5 border-b border-line bg-2/30 rounded-t-xl shrink-0">
                    <h3 className="text-xl font-bold text-p-text flex items-center gap-2">
                        <IoShieldCheckmark className="text-[#5652CF]" />
                        Manage User Roles
                    </h3>
                    <button onClick={onClose} className="p-1 rounded-full hover:bg-2 transition-colors">
                        <IoClose className="h-6 w-6 text-s-text" />
                    </button>
                </div>

                {/* Body */}
                <div className="p-6 overflow-y-auto custom-scrollbar flex-1">
                    <div className="mb-4">
                        <p className="text-sm text-s-text">
                            Assign roles and permissions to <strong className="text-p-text">{user.name}</strong> ({user.email}).
                        </p>
                    </div>

                    {isLoading ? (
                        <div className="flex justify-center items-center h-32">
                            <div className="w-8 h-8 border-4 border-[#5652CF] border-t-transparent rounded-full animate-spin"></div>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {allRoles.length === 0 ? (
                                <p className="text-sm text-center text-s-text bg-2 p-4 rounded-lg">No roles defined in the system.</p>
                            ) : (
                                allRoles.map(role => {
                                    const roleId = role.id || role._id;
                                    const isSelected = selectedRoleIds.has(roleId);

                                    return (
                                        <div
                                            key={roleId}
                                            onClick={() => handleToggleRole(roleId)}
                                            className={`p-4 rounded-lg border-2 cursor-pointer transition-colors flex items-start gap-3 ${isSelected ? 'border-[#5652CF] bg-[#5652CF]/5' : 'border-line bg-2/50 hover:border-s-text'}`}
                                        >
                                            <div className="pt-0.5">
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={() => { }} // Handled by div wrapper
                                                    className="w-4 h-4 text-[#5652CF] bg-1 border-line rounded focus:ring-[#5652CF] focus:ring-2"
                                                />
                                            </div>
                                            <div className="flex-1">
                                                <div className="flex items-center justify-between">
                                                    <span className="font-bold text-p-text text-sm">{role.name}</span>
                                                    {role.is_system_role && (
                                                        <span className="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-purple-500/10 text-purple-400 border border-purple-500/20">System</span>
                                                    )}
                                                </div>
                                                <p className="text-xs text-s-text mt-1">{role.description || "No description provided."}</p>
                                                <p className="text-xs text-blue-400 mt-1.5 font-medium">{role.permissions?.length || 0} permissions granted</p>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end p-5 border-t border-line bg-2/30 rounded-b-xl gap-3 shrink-0">
                    <button
                        onClick={onClose}
                        className="px-5 py-2.5 text-sm font-semibold text-s-text bg-transparent border border-line rounded-lg hover:bg-2 hover:text-p-text transition-all"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={isSaving || isLoading}
                        className="flex items-center justify-center gap-2 px-6 py-2.5 text-sm font-bold text-white bg-blue-600 rounded-lg hover:bg-[#5652CF] disabled:opacity-50 disabled:cursor-wait shadow-lg shadow-blue-500/20 transition-all hover:-translate-y-0.5"
                    >
                        <IoSaveOutline className="text-lg" />
                        {isSaving ? 'Saving...' : 'Save Roles'}
                    </button>
                </div>
            </div>
        </div>
    );
};
